<template>

  <div class='card rounded'>
    <div class='card-body rounded bg-light'>
      <div v-if="flashMessage" class="flash-messages">
        <div class="alert alert-info alert-dismissible fade show">
          {{ flashMessage }}
          <button type='button' class="btn-close" aria-label='Close' @click='flashMessage = null'></button>
        </div>
      </div>
      <h2>{{ $t("backend.e2e.threshold_ceremony.index.trustee_ceremony")}}</h2>
      <div class='hstack flex-wrap'>
        <div>
          <div class="d-flex align-items-center small"><b class="me-2">{{ $t(`activerecord.attributes.e2e/orchestrator.uuid`) }}</b> {{ orchestrator.uuid }}</div>
          <div class="d-flex align-items-center small"><b class="me-2">{{ $t(`activerecord.attributes.e2e/orchestrator.workflow_state`) }}</b> {{ $t(`backend.attributes.orchestrator.workflow_states.${status}`) }}</div>
          <div v-if="orchestrator.threshold" class="d-flex align-items-center small">
            <b class="me-2">{{ $t("activerecord.attributes.e2e/orchestrator.threshold") }}</b> {{ orchestrator.threshold }}
          </div>
        </div>

        <div class="hstack align-items-end ms-auto ">
          <div v-if="status === 'sign_up'" class="d-flex gap-2 me-2">
            <input type="number" :placeholder="$t('backend.placeholders.orchestrator.threshold')" class="form-control" v-model="threshold" />
            <button class="btn btn-primary" @click="setThreshold()">{{ $t('backend.attributes.orchestrator.workflow_events.set_threshold') }}</button>
            <button class="btn btn-danger" @click="stopCeremony()">{{ $t('backend.attributes.orchestrator.workflow_events.stop_ceremony') }}</button>
          </div>
          <button v-if="status === 'initial'" class="btn btn-primary" @click="startCeremony()">{{ $t('backend.attributes.orchestrator.workflow_events.start_ceremony') }}</button>
          <button v-if="['public_coefficients', 'partial_secrets', 'validations'].includes(status)" class="btn btn-warning me-2" @click="retryStep()">{{ $t('backend.attributes.orchestrator.workflow_events.retry') }}</button>
          <button v-if="orchestrator.threshold && !election?.boardSlug" class="btn btn-danger" @click="resetCeremony()">
            {{ $t('backend.e2e.threshold_ceremony.index.reset_ceremony') }}
          </button>
        </div>
      </div>

      <hr>
      <div>
        <div class="dropdown float-end" v-if="!orchestrator.threshold && status == 'sign_up'">
          <button
            class="btn btn-primary dropdown-toggle"
            type="button"
            id="dropdownMenuButton"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {{ $t('backend.e2e.threshold_ceremony.index.add_trustee') }}
          </button>

          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <h6 class="dropdown-header">
              {{ $t('backend.e2e.threshold_ceremony.index.automated_trustees') }}
            </h6>

            <i v-if='systemTrustees.length === 0' class="dropdown-item-text small text-muted">{{ $t('backend.e2e.threshold_ceremony.index.no_trustees_to_add') }}</i>

            <template v-else>
              <button
                v-for="trusteeService in invitableSystemTrustees"
                class="dropdown-item"
                @click="inviteSystemTrustee(trusteeService.id)"
              >
                {{ trusteeService.name }}
              </button>
            </template>
            <div class='dropdown-divider'></div>
            <h6 class="dropdown-header">{{ $t('backend.e2e.threshold_ceremony.index.human_trustees') }}</h6>
            <button class="dropdown-item" @click='openInviteModal'>
              {{ $t('backend.e2e.threshold_ceremony.index.invite_human_trustee') }}
            </button>
          </div>
        </div>
        <div v-if='currentCeremonyFinished' class='hstack align-items-center mt-4 justify-content-center'>
          <i class='fas fa-2x me-3 fa-check text-success'></i>
          <h2 class='text-success'>{{ $t(`backend.attributes.orchestrator.workflow_states.ceremony_finished`) }}</h2>
        </div><h3 class='mb-3'>
          {{ $t('activerecord.models.e2e/trustee.other') }}
        </h3>

        <div v-if='trustees.length == 0' class='text-center p-2'>
          <h5 class='fst-italic'>{{ $t("backend.e2e.threshold_ceremony.index.no_trustees") }}</h5>

          <span class='small'>{{ $t("backend.e2e.threshold_ceremony.index.start_ceremony_prompt") }}</span>
        </div>
        <div class='vstack gap-3'>
          <div v-for="(trustee, index) in trustees" class='bg-white p-3 rounded'>
            <div class='hstack align-items-center justify-content-between'>
              <div class='hstack align-items-center'>
                <div :class="trustee.connected ? 'bg-success pulse-success' : 'bg-danger' " class="circle-color me-3"
                    v-tooltip="trustee.connected ? $t('backend.e2e.voting_round_reports.show.present') : $t('backend.e2e.voting_round_reports.show.not_present')"></div>
                <h4 class='mb-0'>{{ trustee.name }}</h4><span v-if='trustee.email' class='ms-3 small text-muted'><{{trustee.email}}></span>
              </div>
              <div v-if="!orchestrator.threshold && status == 'sign_up'">
                <div v-if="trustee.mandatory" class="badge text-bg-primary float-end">{{ $t("backend.e2e.threshold_ceremony.index.mandatory_participant") }}</div>
                  <template v-else>
                    <div class='hstack gap-2 float-end'>
                      <button @click='openUpdateInviteModal(index)' class='btn btn-primary' v-tooltip="$t('backend.actions.edit')">
                        <i class='fas fa-edit'></i>
                      </button>
                      <button @click='resendEmail(trustee.id)' class='btn btn-primary' v-tooltip="$t('backend.actions.retry')">
                        <i class='fas fa-envelope'></i>
                      </button>
                      <button @click='deleteTrustee(trustee.id)' class='btn btn-danger' v-tooltip="$t('backend.actions.delete')">
                        <i class='fas fa-trash'></i>
                      </button>
                    </div>

                    <Modal id='updateHumanTrusteeModal' @close='closeUpdateInviteModal' :visible='inviteUpdateModalActive' maxWidth="500px" :closable="true">
                      <template #header>
                        <div class="modal-title text-muted">
                          {{ $t("backend.e2e.threshold_ceremony.index.edit_human_trustee") }}
                        </div>
                      </template>
                      <template #body>
                        <form @submit.prevent='updateHumanTrustee'>
                          <div class='form-floating mb-3'>
                            <input :class="{'is-invalid': inviteErrors?.name }" :placeholder="$t('activerecord.attributes.e2e/trustee.name')" type="text" class="form-control" v-model="humanTrusteeName" />
                            <label>{{ $t('activerecord.attributes.e2e/trustee.name') }}</label>
                            <div v-if="inviteErrors?.name" class="invalid-feedback">{{ inviteErrors.name.map(error => error.message ).join(", ") }}</div>
                          </div>
                          <div class='form-floating mb-3'>
                            <input :class="{'is-invalid': inviteErrors?.email }" :placeholder="$t('activerecord.attributes.e2e/trustee.email')" type="text" class="form-control" v-model="humanTrusteeEmail" />
                            <label>{{ $t('activerecord.attributes.e2e/trustee.email') }}</label>
                            <div v-if="inviteErrors?.email" class="invalid-feedback">{{ inviteErrors.email.map(error => error.message ).join(", ") }}</div>
                          </div>
                          <select class="form-select" v-model='humanTrusteeLocale'>
                            <option v-for="locale in locales" :value="locale">
                              {{ $t("js.language_name", locale) }}
                            </option>
                          </select>
                        </form>
                      </template>
                      <template #footer>
                        <button type="button" class="btn btn-secondary" @click="closeUpdateInviteModal"> {{ $t("backend.e2e.threshold_ceremony.index.close") }}</button>
                        <button type="button" class="btn btn-primary" @click="updateHumanTrustee">{{ $t("backend.e2e.threshold_ceremony.index.save") }}</button>
                      </template>
                    </Modal>

                  </template>
              </div>
            </div>
            <div v-if="status !=  'sign_up'" class="hstack align-items-start gap-3 flex-wrap mt-2">
              <div v-for="(trusteeStatus, index) in Object.values(trusteeSteps)">
                <div class="hstack align-items-center me-3 gap-2">
                  <i :class="trusteeStepIcons[index]" class="fas"></i>
                  <span>{{ $t(`backend.attributes.trustee.ceremony_progress.${trusteeStatus}`) }}</span>
                  <i
                    v-if="Object.values(trusteeSteps).findIndex((s) => s == trustee.status) >= index"
                    class="fas fa-check text-success"
                  ></i>
                  <i
                    v-if="Object.values(trusteeSteps).findIndex((s) => s == trustee.status) === index - 1 && status !== 'sign_up' && trustee.connected"
                      class="fas fa-spin fa-spinner"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="status ==  'sign_up' && trustees.length < 2" class="text-center text-danger mt-3">
          <h5><i class="fa fa-exclamation-triangle me-2"></i> {{ $t('backend.e2e.threshold_ceremony.index.no_trust.header') }}</h5>
          <p class="mb-0">{{ $t('backend.e2e.threshold_ceremony.index.no_trust.body') }}</p>
        </div>
    </div>
  </div>

    <Modal id='humanTrusteeModal' @close='closeInviteModal' :visible='inviteModalActive' maxWidth="500px" :closable="true">
      <template #header>
        <div class="modal-title text-muted">
          Invite
        </div>
      </template>
      <template #body>
        <form @submit.prevent='inviteHumanTrustee'>
          <div class='form-floating mb-3'>
            <input :class="{'is-invalid': inviteErrors?.name }" :placeholder="$t('activerecord.attributes.e2e/trustee.name')" type="text" class="form-control" v-model="humanTrusteeName" />
            <label>{{ $t('activerecord.attributes.e2e/trustee.name') }}</label>
            <div v-if="inviteErrors?.name" class="invalid-feedback">{{ inviteErrors.name.map(error => error.message ).join(", ") }}</div>
          </div>
          <div class='form-floating mb-3'>
            <input :class="{'is-invalid': inviteErrors?.email }" :placeholder="$t('activerecord.attributes.e2e/trustee.email')" type="text" class="form-control" v-model="humanTrusteeEmail" />
            <label>{{ $t('activerecord.attributes.e2e/trustee.email') }}</label>
            <div v-if="inviteErrors?.email" class="invalid-feedback">{{ inviteErrors.email.map(error => error.message ).join(", ") }}</div>
          </div>
          <select class="form-select" v-model='humanTrusteeLocale'>
            <option v-for="locale in locales" :value="locale">
              {{ $t("js.language_name", locale) }}
            </option>
          </select>
        </form>
      </template>
      <template #footer>
        <button type="button" class="btn btn-secondary" @click="closeInviteModal">{{ $t("backend.e2e.threshold_ceremony.index.close") }}</button>
        <button type="button" class="btn btn-primary" @click="inviteHumanTrustee">{{ $t("backend.e2e.threshold_ceremony.index.invite") }}</button>
      </template>
    </Modal>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { CeremonyChannelSubscription } from '@/channels/CeremonyChannel';
import Modal from '@/components/shared/Modal.vue';
import apiClient from '@/entrypoints/frontend/apiClient';
import BallotEmbedded from '@/components/frontend/shared/BallotEmbedded.vue';
import type { ResultOrchestrator, ElectaTrustee, PropType, Locale } from '@/types';

export default defineComponent({
  name: 'ThresholdCeremony',
  components: { BallotEmbedded, Modal },
  props: {
    initData: Object,
    orchestratorId: {
      type: Number,
      required: true,
    },
    systemTrustees: Array,
    locales: {
      type: Array as PropType<Locale[]>,
      required: true,
    },
  },
  data() {
    return {
      election: null,
      orchestrator: {
        uuid: null,
        threshold: null,
      } as ResultOrchestrator,
      trustees: [] as Array<ElectaTrustee>,
      status: '',
      threshold: null,
      humanTrusteeEmail: '',
      humanTrusteeName: '',
      humanTrusteeLocale: '',
      uuid: '',
      trusteeSteps: {
        0: 'generated_coefficients',
        1: 'generated_secrets',
        2: 'validated_secrets',
      } as Record<number, string>,
      trusteeStepIcons: {
        0: 'fa-calculator',
        1: 'fa-key',
        2: 'fa-clipboard-check',
      } as Record<number, string>,
      currentCeremonyFinished: false,
      inviteModalActive: false,
      inviteUpdateModalActive: false,
      inviteErrors: null,
      currentIndex: null,
      flashMessage: null,
    };
  },
  computed: {
    trusteeServiceIds() {
      return this.trustees.map(t => t.trustee_service_id)
    },
    invitableSystemTrustees() {
      return this.systemTrustees.filter(t => !this.trusteeServiceIds.includes(t.id))
    }
  },
  methods: {
    handleReceived(data) {
      switch (data.type) {
        case 'full_update':
          this.election = data.state.election;
          this.status = data.state.status;
          this.orchestrator = data.state.orchestrator;
          this.trustees = data.state.trustees;
          break;
        default:
          this.subscription.fetchStatus('status');
      }
    },
    async setThreshold() {
      await apiClient.post('threshold_ceremony/set_threshold', { threshold: this.threshold });
    },
    async retryStep() {
      await apiClient.post('threshold_ceremony/retry');
      this.currentCeremonyFinished = false
    },
    async resetCeremony() {
      await apiClient.post('threshold_ceremony/reset');
      this.currentCeremonyFinished = false
    },
    async stopCeremony() {
      await apiClient.post('threshold_ceremony/stop_ceremony');
    },
    async startCeremony() {
      await apiClient.post('threshold_ceremony/start_ceremony');
    },
    async inviteSystemTrustee(id) {
      await apiClient.post('trustees/invite_system_trustee', { trustee_service_id: id });
    },
    async inviteHumanTrustee() {
      await apiClient.post('trustees/invite_human_trustee', {
        headers: {
          'Content-Type': 'application/json'
        },
        e2e_human_trustee: {
          name: this.humanTrusteeName,
          email: this.humanTrusteeEmail,
          locale: this.humanTrusteeLocale,
        },
      }).then(resp => {
        if (resp.status == 201) {
          this.closeInviteModal()
          this.flashMessage = this.$t("backend.e2e.threshold_ceremony.index.flash_responses.trustee_invited")
        }
      }).catch(error => {
        this.inviteErrors = error.response.data.errors
      });

      this.humanTrusteeEmail = '';
    },
    async resendEmail(id) {
      await apiClient.post('trustees/resend_email', { id: id })
        .then(resp => {
          if(resp.status == 201) {
            this.flashMessage = this.$t("backend.e2e.threshold_ceremony.index.flash_responses.email_sent")
          }
        });
    },
    async deleteTrustee(id: number) {
      await apiClient.delete(`trustees/${id}`)
        .then(resp => {
          if(resp.status == 204) {
            this.flashMessage = this.$t("backend.e2e.threshold_ceremony.index.flash_responses.trustee_removed")
          }
        })
    },
    async updateHumanTrustee() {
      const id =  this.trustees[this.currentIndex].id
      await apiClient.patch(`trustees/${id}`, {
        headers: {
          'Content-Type': 'application/json'
        },
        e2e_human_trustee: {
          name: this.humanTrusteeName,
          email: this.humanTrusteeEmail,
          locale: this.humanTrusteeLocale,
        },
      }).then(resp => {
        if (resp.status == 204) this.closeUpdateInviteModal()
      }).catch(error => {
        this.inviteErrors = error.response.data.errors
      });
    },
    closeInviteModal() {
      this.inviteErrors = null;
      this.inviteModalActive = false;
    },
    openInviteModal() {
      this.inviteModalActive = true;
      this.inviteErrors = null;
      this.humanTrusteeEmail = ''
      this.humanTrusteeName = ''
      this.humanTrusteeLocale = this.locales[0]
    },
    closeUpdateInviteModal() {
      this.inviteErrors = null;
      this.inviteUpdateModalActive = false;
    },
    openUpdateInviteModal(index){
      this.currentIndex = index
      this.inviteUpdateModalActive = true
      this.humanTrusteeEmail = this.trustees[index].email
      this.humanTrusteeName = this.trustees[index].name
      this.humanTrusteeLocale = this.trustees[index].locale
      this.flashMessage = this.$t("backend.e2e.threshold_ceremony.index.flash_responses.trustee_updated")
    },
  },
  watch: {
    status: function(newValue, oldValue) {
      if(newValue == "ceremony_finished" && oldValue != '') {
        this.currentCeremonyFinished = true
      }
    }
  },
  created() {
    this.status = this.initData.status;
    this.orchestrator = this.initData.orchestrator;
    this.trustees = this.initData.trustees;
    this.subscription = new CeremonyChannelSubscription(this.orchestratorId,  {
      received: this.handleReceived,
    });
  },
});
</script>
